import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

declare let $: any;
declare let window: any;
declare let html2pdf: any;

@Component({
  selector: "app-azure-billing-services",
  templateUrl: "./azure-billing-services.component.html",
  styleUrls: ["./azure-billing.component.css"],
})
export class AzureBillingServicesComponent implements OnInit, OnDestroy {
  userId = localStorage.getItem("selectedClientList");
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  subscriptionId = "";
  resourceGroup = "";
  monthYear = "";
  currency = "";
  loading = false;
  errorMessage: string = null;
  rows: any[] = [];

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private route: ActivatedRoute,
    private router: Router,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.subscriptionId =
      this.route.snapshot.paramMap.get("subscriptionId") || "";
    this.monthYear =
      this.route.snapshot.queryParamMap.get("month_year") ||
      moment().format("YYYY-MM");
    this.currency = this.route.snapshot.queryParamMap.get("currency") || "";
    this.resourceGroup =
      this.route.snapshot.queryParamMap.get("resource_group") || "";
    this.initMonthPicker();
    this.load();
  }

  ngOnDestroy(): void {
    if ($(".datetimepicker-services").data("datepicker")) {
      $(".datetimepicker-services").datepicker("destroy");
    }
  }

  initMonthPicker() {
    $(".datetimepicker-services")
      .datepicker({
        autoclose: true,
        minViewMode: 1,
        format: "mm-yyyy",
        orientation: "bottom auto",
        endDate: new Date(),
      })
      .on("changeDate", (selected: any) => {
        const selectedFormatted = moment(selected["dates"][0]).format("MM-YYYY");
        $(".datetimepicker-services").val(selectedFormatted);
        this.zone.run(() => {
          this.monthYear = moment(selectedFormatted, "MM-YYYY").format("YYYY-MM");
          this.load();
        });
      });

    $(".datetimepicker-services").val(
      moment(this.monthYear, "YYYY-MM").format("MM-YYYY")
    );
  }

  async load() {
    this.loading = true;
    this.errorMessage = null;
    this.rows = [];

    const header = {
      Authorization: localStorage.getItem("t"),
    };

    const apiUrl =
      `${environment.apiURL}/${this.urlPrefix}/azure/billing/billing/services` +
      `?user_id=${encodeURIComponent(this.userId)}` +
      `&subscription_id=${encodeURIComponent(this.subscriptionId)}` +
      `&resource_group=${encodeURIComponent(this.resourceGroup)}` +
      `&month_year=${encodeURIComponent(this.monthYear)}` +
      `&currency=${encodeURIComponent(this.currency)}`;

    const result: any = await this.apiService.getDataPromis(apiUrl, {}, header);

    if (Array.isArray(result?.items)) {
      this.rows = result.items;
    } else {
      this.errorMessage =
        result?.error_message ||
        result?.message ||
        "Unable to load Azure billing services.";
      this.notifier.alert("Error", "", this.errorMessage, "error", 5000);
    }

    this.loading = false;
  }

  goBack() {
    this.router.navigate(
      ["/dash/azure/billing", this.subscriptionId, "resource-groups"],
      {
        queryParams: {
          month_year: this.monthYear,
          currency: this.currency,
        },
      }
    );
  }

  openServiceBreakdown(row: any) {
    this.router.navigate(
      ["/dash/azure/billing", this.subscriptionId, "service-breakdown"],
      {
        queryParams: {
          month_year: this.monthYear,
          currency: row?.currency || this.currency,
          resource_name: this.resourceGroup,
          service_name: row?.service_name || "",
        },
      }
    );
  }

  getTotalCost(): string {
    const total = this.rows.reduce(
      (acc: number, row: any) => acc + (+row.cost || 0),
      0
    );
    return total.toFixed(2);
  }

  async exportPDFTrigger(event: any) {
    this.notifier.loading(true);
    try {
      await this.exportPDF(event);
    } finally {
      this.notifier.loading(false);
    }
  }

  async loadEC2Ddata(event: any) {
    this.notifier.loading(true);
    try {
      await this.exportCSV(event);
    } finally {
      this.notifier.loading(false);
    }
  }

  exportCSV(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.rows?.length) {
      return;
    }

    const element = document.createElement("table");
    const { startOf, endOf } = this.getBillingPeriod();
    const total = this.getTotalCost();
    element.innerHTML = `
      <thead>
        <tr>
          <th>Subscription ID</th>
          <th>${this.subscriptionId}</th>
          <th></th>
        </tr>
        <tr>
          <th>Resource Group</th>
          <th>${this.resourceGroup}</th>
          <th></th>
        </tr>
        <tr>
          <th>Month</th>
          <th>${moment(this.monthYear, "YYYY-MM").format("MM-YYYY")}</th>
          <th></th>
        </tr>
        <tr>
          <th>Billing Period</th>
          <th>${startOf} - ${endOf}</th>
          <th></th>
        </tr>
        <tr>
          <th></th>
          <th></th>
          <th></th>
        </tr>
        <tr>
          <th>S.No</th>
          <th>Service Name</th>
          <th>Cost</th>
        </tr>
      </thead>
      <tbody>
        ${this.rows
          .map(
            (row: any, index: number) => `
          <tr>
            <td>${index + 1}</td>
            <td>${row.service_name || "-"}</td>
            <td>${this.formatCost(row.cost)}</td>
          </tr>
        `
          )
          .join("")}
        <tr>
          <td></td>
          <td>Total</td>
          <td>${total}</td>
        </tr>
      </tbody>
    `;

    window.exportToExcelElement(
      element,
      `azure-services-${this.subscriptionId}_${startOf}-${endOf}`,
      "csv"
    );
  }

  exportPDF(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.rows?.length) {
      return;
    }

    const element = document.createElement("div");
    const { startOf, endOf } = this.getBillingPeriod();
    const total = this.getTotalCost();
    element.innerHTML = `
      <div
        id="completebillDetailsTable"
        class="container"
        style="
          margin-bottom: 100px;
          font-weight: 400;
          padding: 0px 25px;
          color: #000000;
          font-family: Arial;
        "
      >
        <div>
          <div class="row">
            <div class="col-sm-5">
              <div style="margin: auto">
                <img
                  style="margin-top: 80px"
                  src="../assets/img/minfy_logo.png"
                  alt="company logo"
                  width="75%"
                />
              </div>
            </div>
            <div class="col-sm-7" style="display: flex; flex-direction: column">
              <div style="padding-bottom: 5px; border-bottom: 3px solid gainsboro">
                <p style="font-weight: 780; font-size: 15px">Azure Usage Report</p>
                <p style="font-size: 12px">
                  For any billing related queries. email us at
                  <span style="color: blue"> billing-support@minfytech.com </span>
                </p>
              </div>
              <div style="padding-bottom: 5px; border-bottom: 3px solid gainsboro">
                <div style="display: flex; flex-direction: row; justify-content: space-between;">
                  <p style="font-size: 12px">Azure Service Charges</p>
                  <p style="font-size: 12px">$${total}</p>
                </div>
              </div>
              <div style="padding-bottom: 5px; border-bottom: 3px solid gainsboro">
                <div
                  style="
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    font-weight: bold;
                    font-size: 15.5px;
                  "
                >
                  <p>TOTAL AMOUNT <span style='font-size: 0.8em; color: inherit;'>(Excluding tax)</span></p>
                  <p>$${total}</p>
                </div>
              </div>
            </div>

            <div class="col-sm-12" style="font-size: 12px">
              <p><span style='font-weight: bold;'>Subscription: </span><span>${this.subscriptionId}</span></p>
              <p><span style='font-weight: bold;'>Resource Group: </span><span>${this.resourceGroup}</span></p>
              <p style='font-weight: bold;'>
                This Azure Usage Report is for the billing period
                <span>${startOf} - ${endOf}</span>
              </p>
              <p style="line-height: 1.5; margin-bottom: 5px; font-size: 11px">
                Greetings from MINFY, we're writing to provide you with an electronic
                report for your use of Azure services. Additional information about your
                bill, individual service charge details, and your account history are
                available on Swayam Portal.
              </p>
            </div>

            <table
              class="table"
              id="completepdfTable"
              style="
                border: 1.5px solid #d1d1a1;
                width: 720px;
                float: left;
                font-size: 12px;
                margin: 10px;
                color: #000000;
              "
            >
              <thead>
                <tr style="border-bottom: 1px solid #d1d1a1">
                  <th style="text-align: left; background-color: #c8e1f5">
                    Service Breakup Summary
                  </th>
                  <th style="background-color: #c8e1f5"></th>
                </tr>
              </thead>
              <tbody style="font-size: 12px">
                <tr style="border-bottom: 1px solid #d1d1a1; font-weight: bold">
                  <td style="color: #fcb65c">Azure Service Charges</td>
                  <td style="text-align: right">$${total}</td>
                </tr>
                ${this.rows
                  .map(
                    (row: any) => `
                  <tr style="border-bottom: 1px solid #d1d1a1">
                    <td style="padding-left: 30px">${row.service_name || "-"}</td>
                    <td style="text-align: right">$${this.formatCost(row.cost)}</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
              <tfoot style="background-color: #cccc98; color: #000000">
                <tr>
                  <td style="text-align: left"><b>Total</b></td>
                  <td style="text-align: right; color: #000000;"><b>$${total}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    `;

    const opt = {
      margin: 5,
      filename: `${this.subscriptionId}_${startOf}-${endOf}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { dpi: 192, scale: 4, letterRendering: true, useCORS: true },
      pagebreak: {
        before: ".newPage",
        avoid: ["h2", "h3", "h4", "td", "tr", ".field"],
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  }

  formatCost(value: any): string {
    return (+value || 0).toFixed(2);
  }

  getBillingPeriod(): { startOf: string; endOf: string } {
    const selectedMonth = moment(this.monthYear, "YYYY-MM");
    const startOf = selectedMonth.startOf("month").format("DD-MM-YYYY");
    const endOf = selectedMonth.isSame(moment(), "month")
      ? moment().format("DD-MM-YYYY")
      : selectedMonth.endOf("month").format("DD-MM-YYYY");

    return { startOf, endOf };
  }
}
